<template>
  <div class="bank-verification-step">

    <!-- Context header -->
    <div class="bv-header">
      <div class="bv-header__icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9L12 3L21 9V21H15V15H9V21H3V9Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="bv-header__text">
        <h3 class="bv-header__title">Connect your bank account</h3>
        <p class="bv-header__sub">We securely connect to your bank to verify income and expenses. This helps us assess your application faster and more accurately.</p>
      </div>
    </div>

    <!-- Trust badges -->
    <div class="bv-trust-bar">
      <div class="bv-trust-item">
        <svg viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/>
        </svg>
        <span>Read-only access</span>
      </div>
      <div class="bv-trust-item">
        <svg viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524z"/>
        </svg>
        <span>Bank-grade encryption</span>
      </div>
      <div class="bv-trust-item">
        <svg viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
          <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
        </svg>
        <span>No login credentials stored</span>
      </div>
    </div>

    <!-- CreditSense iframe container -->
    <div class="bv-iframe-wrap">
      <iframe id="creditSenseIFrame" ref="csIframe" class="bv-iframe" src="about:blank">
        iframes are not supported in this browser
      </iframe>
    </div>

  </div>
</template>

<script>
import NewApplicationStepMixin from '@modules/guest/new-applicant/components/mixins/NewApplicationStepMixin';
import {scrollToErrorFormField} from '@modules/shared/helpers/form';
import store from '@modules/guest/new-applicant/store/store';
import {NEW_APPLICATION} from '@store/constants';
import {loadScript} from '@modules/shared/helpers/script-loader';
import {debug, IS_DEBUG_MODE, IS_PRODUCTION} from '@/utils';
import {sendBankVerification} from '@modules/guest/new-applicant/NewApplicationService';

const ERROR_CONTAINER_NAME = 'NewApplicationBankVerificationStepForm';
const CS_SCRIPT_URI = 'https://6dadc58e31982fd9f0be-d4a1ccb0c1936ef2a5b7f304db75b8a4.ssl.cf4.rackcdn.com/CS-Integrated-Iframe-v1.min.js';

export default {
  name: 'BankVerification',
  mixins: [NewApplicationStepMixin],
  computed: {
    code() { return store.state[NEW_APPLICATION.MODULE_NAME].code; },
    loanNumber() { return store.state[NEW_APPLICATION.MODULE_NAME].meta.loanNumber; },
    canPerformCS() { return store.state[NEW_APPLICATION.MODULE_NAME].meta.canPerformCS; },
    shouldInitialize() { return typeof this.loanNumber === 'string' && this.canPerformCS; },
  },
  data() { return { initialized: false }; },
  watch: {
    shouldInitialize: {
      immediate: true,
      handler(value) { if (value) this.initCS(); },
    },
  },
  methods: {
    onNext() {
      if (!this.shouldGoNextStep()) { scrollToErrorFormField(this.$el); return; }
      store.dispatch(NEW_APPLICATION.ACTIONS.NEXT_STEP);
    },
    onPrev() { store.dispatch(NEW_APPLICATION.ACTIONS.PREV_STEP); },
    async initCS() {
      if (this.initialized || !this.loanNumber) return;
      await loadScript(CS_SCRIPT_URI);
      this.initialized = true;

      let storeCode = IS_PRODUCTION ? 'FLMY01' : 'FLMY02';
      let debugBanks = IS_DEBUG_MODE;
      let appId = null;

      $.CreditSense.Iframe({
        client: storeCode,
        elementSelector: '#creditSenseIFrame',
        enableDynamicHeight: false,
        params: { appRef: this.loanNumber, uniqueAppRef: true, debugBanks },
        callback: (response, data) => {
          switch (response) {
            case '3':   debug('Application id generated'); appId = data; break;
            case '99':  debug('Bank details collected successfully'); break;
            case '100': this.applicationCompleted(appId); break;
          }
        },
      });
    },
    async applicationCompleted(appId) {
      try { await sendBankVerification(this.code, {appId}); }
      catch {} finally { await store.dispatch(NEW_APPLICATION.ACTIONS.NEXT_STEP); }
    },
  },
  created() { this.registerErrorContainer(ERROR_CONTAINER_NAME); },
};
</script>

<style lang="scss" scoped>
$amber:    #FFB000;
$ink:      #15171C;
$ink-soft: #5B606B;
$ink-faint:#9197A1;
$line:     #E7E4DA;
$surface:  #FBFAF6;

.bank-verification-step {
  .bv-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.25rem;

    &__icon {
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      border-radius: 12px;
      background: rgba(255, 176, 0, 0.1);
      border: 1.5px solid rgba(255, 176, 0, 0.25);
      display: flex;
      align-items: center;
      justify-content: center;
      color: $amber;

      svg { width: 22px; height: 22px; }
    }

    &__text { flex: 1; }

    &__title {
      font-size: 1.05rem;
      font-weight: 700;
      color: $ink;
      margin: 0 0 4px;
    }

    &__sub {
      font-size: 13.5px;
      color: $ink-soft;
      margin: 0;
      line-height: 1.55;
    }
  }

  .bv-trust-bar {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
  }

  .bv-trust-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: $ink-soft;
    background: $surface;
    border: 1px solid $line;
    border-radius: 100px;
    padding: 5px 12px;

    svg { width: 12px; height: 12px; color: #22c55e; }
  }

  .bv-iframe-wrap {
    border: 1.5px solid $line;
    border-radius: 14px;
    overflow: hidden;
    background: #fff;
  }

  .bv-iframe {
    width: 100%;
    height: 40rem;
    border: none;
    display: block;
  }
}
</style>
